import { FaEnvelope, FaPhone } from "react-icons/fa";
import { motion } from "framer-motion";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import Button from "@components/Button";
import InputField from "@components/InputField";
import { IoChevronForward } from "react-icons/io5";
import SectionHeader from "@components/SectionHeader";

const cardVariants = {
	hidden: { opacity: 0, y: 50 },
	visible: { opacity: 1, y: 0 },
};

function Contact() {
	const [mapsLoaded, setMapsLoaded] = useState({
		act: false,
		queanbeyan: false,
	});

	const [tempFormData, setTempFormData] = useState({
		email: "",
		phone: "",
		message: "",
	});
	const [error, setError] = useState(null);
	const [showSubmitted, setShowSubmitted] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setTempFormData((prev) => ({ ...prev, [name]: value }));
		// Clear error when user starts typing
		if (error) setError(null);
	};

	const handleFormSubmit = async (e) => {
		e.preventDefault();

		// Validate required fields
		if (!tempFormData.email || !tempFormData.message) {
			setError("Please fill in all required fields.");
			return;
		}

		// Validate email format
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(tempFormData.email)) {
			setError("Please enter a valid email address.");
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			// Prepare form data for submission
			const combinedData = {
				email: tempFormData.email.trim(),
				phone: tempFormData.phone.trim(),
				message: tempFormData.message.trim(),
			};

			console.log("Sending form data:", combinedData);

			// Use the full URL for the PHP backend to avoid CORS issues
			const response = await axios.post("/send_contact.php", combinedData);

			console.log("Response received:", response);

			// Check if the response indicates success
			// You should adapt these conditions based on your PHP script's response format
			if (response.status === 200 && response.data) {
				// Check for success indicators in the response data
				if (
					response.data.success === true ||
					response.data.status === "success" ||
					response.data.message === "success" ||
					(!response.data.error && !response.data.fail)
				) {
					console.log("Email sent successfully:", response.data);

					// Success - show confirmation and reset form
					setShowSubmitted(true);
					setTempFormData({
						email: "",
						phone: "",
						message: "",
					});

					// Auto-hide success message after 5 seconds
					setTimeout(() => {
						setShowSubmitted(false);
					}, 5000);
				} else {
					// Server returned 200 but indicated failure in response body
					const errorMessage =
						response.data.error ||
						response.data.message ||
						"Failed to send message";
					setError(errorMessage);
				}
			} else {
				// Unexpected response status or format
				setError("Unexpected response from server");
			}
		} catch (error) {
			console.error(
				"Error sending email:",
				error.response?.data || error.message,
			);

			// Handle different types of errors
			if (error.response) {
				// Server responded with error status
				const errorMessage =
					error.response.data?.error ||
					error.response.data?.details ||
					error.response.data?.message ||
					"Server error occurred";
				setError(`Error (${error.response.status}): ${errorMessage}`);
			} else if (error.request) {
				// Request was made but no response received
				setError("No response from server. Please check your connection.");
			} else {
				// Something else happened
				setError(`Request error: ${error.message}`);
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div>
			<Helmet>
				<title>Contact Steer Smart Driving School | Get in Touch Today</title>
				<meta
					name="description"
					content="Contact Steer Smart Driving School in Canberra. Get in touch for driving lessons, enquiries, or bookings. We're here to help you start your driving journey."
				/>
				<link rel="canonical" href="https://steersmartdriving.com.au/contact" />
			</Helmet>

			<div className="flex flex-col gap-12 py-6 md:py-12 justify-center items-center">
				<div className="flex flex-col items-center font-generalSans text-center gap-6 px-2 md:px-0">
					<SectionHeader
						title="Get in Touch"
						subtitle="We're here to help you steer in the right direction. Have a question, feedback, or ready to book your driving lessons? Just fill out the form below, call us, or drop us an email. We'll get back to you as soon as we can."
						heading="h1"
					/>
					<Button
						buttonName="Book Now"
						buttonLink="/packages"
						buttonStyle="primary"
					/>
				</div>

				<div className="flex flex-col lg:flex-row font-generalSans gap-12">
					{/* Email Card */}
					<motion.div
						className="gap-6 bg-white/60 backdrop-blur-md flex flex-col group px-6 py-6 rounded-2xl w-full lg:w-[30rem]"
						initial="hidden"
						animate="visible"
						variants={cardVariants}
						transition={{ duration: 0.5 }}
					>
						<div className="flex items-center space-x-4">
							<FaEnvelope className="text-steerBlack text-2xl" />
							<h2 className="font-medium text-gray-900 text-xl xl:lg:text-2xl cursor-default">
								Email
							</h2>
						</div>
						<div className="gap-2 text-sm xl:lg:text-base">
							<p className="font-light cursor-default">You can reach us at:</p>
							<a
								href="mailto:info@steersmartdriving.com.au"
								className="flex items-center gap-2"
							>
								<IoChevronForward />
								<span className="group-hover:-ml-2 duration-300 ease-in transition-all">
									info@steersmartdriving.com.au
								</span>
							</a>
						</div>
					</motion.div>

					{/* Contact Card */}
					<motion.div
						className="gap-6 bg-white/60 backdrop-blur-md flex flex-col group px-6 py-6 rounded-2xl w-full lg:w-[30rem]"
						initial="hidden"
						animate="visible"
						variants={cardVariants}
						transition={{ duration: 0.5, delay: 0.2 }}
					>
						<div className="flex items-center space-x-4">
							<FaPhone className="text-steerBlack text-2xl" />
							<h2 className="font-medium text-gray-900 text-xl xl:lg:text-2xl cursor-default">
								Contact
							</h2>
						</div>
						<div className="space-y-2 text-sm xl:lg:text-base">
							<p className="font-light cursor-default">You can call us at:</p>
							<a href="tel:+61450487804" className="flex items-center gap-2">
								<IoChevronForward />
								<span className="group-hover:-ml-2 duration-300 ease-in transition-all">
									+61 450 487 804
								</span>
							</a>
						</div>
					</motion.div>
				</div>

				{/* Contact Form */}
				<div className="w-full lg:w-1/2 justify-end font-poppins p-8 bg-white/60 backdrop-blur-md rounded-2xl">
					{/* Success Message */}
					{showSubmitted && (
						<div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
							<p className="font-medium">Message sent successfully!</p>
							<p className="text-sm">
								We&apos;ll get back to you as soon as possible.
							</p>
						</div>
					)}

					<form className="grid grid-cols-1 gap-4" onSubmit={handleFormSubmit}>
						{/* Email - Required */}
						<InputField
							label="Email"
							type="email"
							name="email"
							value={tempFormData.email || ""}
							required={true}
							onChange={handleInputChange}
							placeholder="your-email@example.com"
							disabled={isSubmitting}
						/>

						{/* Phone Number - Optional */}
						<InputField
							label="Phone Number"
							type="tel"
							name="phone"
							value={tempFormData.phone || ""}
							required={false}
							onChange={handleInputChange}
							placeholder="Starting with 04 (10 or 11 digits) - Optional"
							disabled={isSubmitting}
						/>

						{/* Message - Required */}
						<InputField
							label="Message"
							type="textarea"
							name="message"
							value={tempFormData.message || ""}
							required={true}
							onChange={handleInputChange}
							placeholder="Any additional details"
							rows={4}
							disabled={isSubmitting}
						/>

						{/* Error Message */}
						{error && (
							<div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
								{error}
							</div>
						)}

						{/* Submit Button */}
						<div className="flex justify-center">
							<Button
								buttonType="submit"
								buttonName={isSubmitting ? "Sending..." : "Send Message"}
								buttonStyle="primary"
								disabled={isSubmitting || showSubmitted}
							/>
						</div>
					</form>
				</div>

				{/* Google Map Iframe */}
				<div className="flex flex-wrap space-x-0 space-y-4 xl:lg:space-y-0 xl:lg:space-x-6 py-6 w-full">
					<div className="flex-1 min-w-[300px] relative">
						{!mapsLoaded.act && (
							<div className="absolute inset-0 bg-gray-200 animate-pulse rounded-lg" />
						)}
						<iframe
							src="https://maps.google.com/maps?width=100%25&amp;height=600&amp;hl=en&amp;q=Australian%20Capital%20Territory&amp;t=&amp;z=15&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
							width="100%"
							height="450"
							style={{ border: 0 }}
							allowFullScreen=""
							loading="lazy"
							title="Google Map ACT"
							className="rounded-lg w-full"
							onLoad={() => setMapsLoaded((prev) => ({ ...prev, act: true }))}
						></iframe>
					</div>
					<div className="flex-1 min-w-[300px] relative">
						{!mapsLoaded.queanbeyan && (
							<div className="absolute inset-0 bg-gray-200 animate-pulse rounded-lg" />
						)}
						<iframe
							src="https://maps.google.com/maps?width=100%25&amp;height=600&amp;hl=en&amp;q=Queanbeyan&amp;t=&amp;z=15&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
							width="100%"
							height="450"
							style={{ border: 0 }}
							allowFullScreen=""
							loading="lazy"
							title="Google Map Queanbeyan"
							className="rounded-lg w-full"
							onLoad={() =>
								setMapsLoaded((prev) => ({ ...prev, queanbeyan: true }))
							}
						></iframe>
					</div>
				</div>
			</div>
		</div>
	);
}

export default Contact;
